import classNames from 'classnames'
import Button from '@/components/ui/button'
import { formatDate, i18n } from '@/locale/i18n'
import { CheckIcon, CrossIcon, InfoIcon } from '@/assets/icons'
import ExclamationTriangleIcon from '@/assets/icons/ExclamationTriangleIcon.jsx'
import { PLACE_BET_BLOCK_REASON } from './placeBetBlockReason'

const iconWrapClass = 'rounded-full w-12 h-12 flex items-center justify-center mb-3'

export default function PlaceBetBlockedContent({
  reason,
  question,
  outcome,
  message = '',
  onSwitchToPlayMoney,
  compact = false,
}) {
  const containerClass = classNames('flex flex-col items-center text-center', {
    'py-6 px-4': !compact,
    'py-4 px-2': compact,
  })

  switch (reason) {
    case PLACE_BET_BLOCK_REASON.REAL_MONEY_NOT_AVAILABLE:
      return (
        <div className={classNames(containerClass, { 'md:max-w-[340px]': !compact })}>
          <div className={classNames(iconWrapClass, 'bg-warning-2')}>
            <ExclamationTriangleIcon fillColor="var(--color-warning-6)" width="16px" height="16px" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6 leading-[133%]">
            {i18n('place_bet.real_money_betting_not_available')}
          </h3>
          <p className="text-sm text-Neutral-4 mb-8">{i18n('place_bet.change_to_play_money_to_place_a_bet')}</p>
          {onSwitchToPlayMoney && (
            <Button size="medium" className="!h-14" onClick={onSwitchToPlayMoney} variant="primary" type="solid">
              {i18n('place_bet.change_to_play_money')}
            </Button>
          )}
        </div>
      )

    case PLACE_BET_BLOCK_REASON.COUNTRY_BLOCKED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-warning-2')}>
            <InfoIcon fillcolor="currentColor" width="16px" height="16px" className="text-warning-5" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('location_not_allowed.title')}</h3>
          <p className="text-sm text-Neutral-4 mb-6">{i18n('location_not_allowed.text_part1')}</p>
          {onSwitchToPlayMoney && (
            <Button size="large" onClick={onSwitchToPlayMoney} variant="primary" type="solid">
              {i18n('location_not_allowed.change_play_money')}
            </Button>
          )}
        </div>
      )

    case PLACE_BET_BLOCK_REASON.PENDING_RESOLUTION:
    case PLACE_BET_BLOCK_REASON.OUTCOME_PAUSED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-informative2-2')}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="text-informative2-6">
              <path
                d="M10.6665 2.66669C11.0347 2.66669 11.3332 2.96517 11.3332 3.33335V6.13335L14.8087 3.70049C14.9595 3.59492 15.1674 3.6316 15.2729 3.78242C15.3121 3.83845 15.3332 3.90518 15.3332 3.97357V12.0265C15.3332 12.2106 15.1839 12.3598 14.9998 12.3598C14.9314 12.3598 14.8647 12.3388 14.8087 12.2996L11.3332 9.86669V12.6667C11.3332 13.0349 11.0347 13.3334 10.6665 13.3334H1.33317C0.964984 13.3334 0.666504 13.0349 0.666504 12.6667V3.33335C0.666504 2.96517 0.964984 2.66669 1.33317 2.66669H10.6665ZM9.99984 4.00002H1.99984V12H9.99984V4.00002ZM4.93317 5.8858C4.98388 5.8858 5.03355 5.90026 5.07634 5.92749L7.97964 7.77502C8.1039 7.85409 8.1405 8.01895 8.06144 8.14322C8.0405 8.17609 8.01257 8.20402 7.97964 8.22502L5.07634 10.0726C4.95208 10.1516 4.78726 10.115 4.7082 9.99075C4.68096 9.94795 4.6665 9.89829 4.6665 9.84755V6.15247C4.6665 6.00519 4.7859 5.8858 4.93317 5.8858ZM13.9998 5.89423L11.3332 7.76069V8.23935L13.9998 10.1058V5.89423Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">
            {i18n('place_bet.this_market_is')}
            <b>{i18n('question_card.date.waiting_result')}</b>
          </h3>
          <p className="text-sm text-Neutral-4">{i18n('place_bet.you_will_recieve_notification')}</p>
        </div>
      )

    case PLACE_BET_BLOCK_REASON.QUESTION_CANCELLED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-[#F4BCB6]')}>
            <CrossIcon fillcolor="currentColor" width="16px" height="16px" className="text-[#580C04]" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('place_bet.market_cancelled')}</h3>
          <p className="text-sm text-Neutral-4 mb-3">{formatDate(question.event_end_date) || ''}</p>
          {question.cancellation_reason && <p className="text-sm text-Neutral-4">{question.cancellation_reason}</p>}
        </div>
      )

    case PLACE_BET_BLOCK_REASON.QUESTION_STOPPED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-negative-5')}>
            <CrossIcon fillcolor="currentColor" width="16px" height="16px" className="text-white" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('place_bet.market_stopped')}</h3>
          <p className="text-sm text-Neutral-4 mb-3">{formatDate(question.event_end_date) || ''}</p>
        </div>
      )

    case PLACE_BET_BLOCK_REASON.QUESTION_RESOLVED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-positive-5')}>
            <CheckIcon fillcolor="currentColor" width="16px" height="16px" className="text-white" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">
            {i18n('place_bet.market_resolved')}:&nbsp;
            {question.outcomes?.find(q => q.id === question.resolution)?.title}
          </h3>
          <p className="text-sm text-Neutral-4 mb-6">
            {formatDate(question.event_end_date || question.resolve_date) || ''}
          </p>
          {(question.brier_score_real_money || question.brier_score_play_money) && (
            <div className="w-full space-y-4 mt-2">
              {question.brier_score_real_money && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-Neutral-4">{i18n('question_card.real_brier_score')}</span>
                  <span className="text-xs font-bold text-Neutral-6">{question.brier_score_real_money}</span>
                </div>
              )}
              {question.brier_score_play_money && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-Neutral-4">{i18n('question_card.play_brier_score')}</span>
                  <span className="text-xs font-bold text-Neutral-6">{question.brier_score_play_money}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )

    case PLACE_BET_BLOCK_REASON.OUTCOME_RESOLVED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-positive-5')}>
            <CheckIcon fillcolor="currentColor" width="16px" height="16px" className="text-white" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">
            {outcome?.title}:&nbsp;
            {outcome?.resolution_display}
          </h3>
          {outcome?.resolution_description && (
            <div className="prose prose-sm max-w-none text-left mt-4 w-full">
              <div dangerouslySetInnerHTML={{ __html: outcome.resolution_description }} />
            </div>
          )}
        </div>
      )

    case PLACE_BET_BLOCK_REASON.OUTCOME_CANCELLED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-[#F4BCB6]')}>
            <CrossIcon fillcolor="currentColor" width="16px" height="16px" className="text-[#580C04]" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('place_bet.market_cancelled')}</h3>
          <p className="text-sm text-Neutral-4 mb-3">{outcome?.title}</p>
        </div>
      )

    case PLACE_BET_BLOCK_REASON.TRADING_PAUSED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-warning-2')}>
            <InfoIcon fillcolor="currentColor" width="16px" height="16px" className="text-warning-5" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('trading_halt.title')}</h3>
          <p className="text-sm text-Neutral-4">{message || i18n('trading_halt.body')}</p>
        </div>
      )

    case PLACE_BET_BLOCK_REASON.OUTCOME_UNPRICED:
      return (
        <div className={containerClass}>
          <div className={classNames(iconWrapClass, 'bg-warning-2')}>
            <InfoIcon fillcolor="currentColor" width="16px" height="16px" className="text-warning-5" />
          </div>
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('place_bet.outcome_unpriced')}</h3>
          <p className="text-sm text-Neutral-4">{i18n('place_bet.outcome_unpriced_description')}</p>
        </div>
      )

    default:
      return null
  }
}
